import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";

// Rotating fun facts about productivity and Omni AI
const LOADING_FACTS = [
    "Omni AI helps you stay on top of your work by aggregating signals from all your tools.",
    "Did you know? The average person spends 28% of their workday on email alone.",
    "Pro tip: Use keyboard shortcuts (Cmd+K) to quickly navigate and take actions.",
    "AI-powered insights can help you identify blockers before they become problems.",
    "Teams using integrated project management tools are 30% more productive.",
    "Omni AI connects GitHub, Slack, Jira, and more to give you a unified view.",
];

interface LoadingScreenProps {
    message?: string;
    fullScreen?: boolean;
    className?: string;
}

export function LoadingScreen({
    message = "Loading...",
    fullScreen = true,
    className = "",
}: LoadingScreenProps) {
    // Start with a random fact
    const [factIndex, setFactIndex] = useState(() =>
        Math.floor(Math.random() * LOADING_FACTS.length)
    );

    // Rotate facts every 4 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            setFactIndex((prev) => (prev + 1) % LOADING_FACTS.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    const content = (
        <div className={`flex flex-col items-center justify-center gap-8 ${className}`}>
            {/* Omni AI Logo with animated glow */}
            <div className="relative">
                {/* Animated glow rings */}
                <motion.div
                    className="absolute inset-0 rounded-2xl bg-gradient-to-r from-zinc-500/30 via-zinc-400/30 to-zinc-500/30 blur-xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                />
                <motion.div
                    className="absolute inset-0 rounded-2xl bg-zinc-400/20 blur-md"
                    animate={{
                        scale: [1.1, 1.3, 1.1],
                        opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 0.3,
                    }}
                />

                {/* Logo container with subtle animation */}
                <motion.div
                    className="relative w-20 h-20 rounded-2xl bg-background flex items-center justify-center shadow-lg border border-border/50"
                    animate={{
                        rotate: [0, 2, -2, 0],
                    }}
                    transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut",
                    }}
                >
                    <img src="/logo.svg" alt="Omni AI" className="w-14 h-14" />
                </motion.div>
            </div>

            {/* Loading text */}
            <div className="flex flex-col items-center gap-2">
                <motion.div
                    className="flex items-center gap-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                >
                    <span className="text-lg font-semibold text-foreground">
                        {message}
                    </span>
                    <motion.span
                        className="flex gap-0.5"
                        animate={{ opacity: [1, 0.5, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground" />
                        <motion.span
                            className="w-1.5 h-1.5 rounded-full bg-foreground"
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                        />
                        <motion.span
                            className="w-1.5 h-1.5 rounded-full bg-foreground"
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                        />
                    </motion.span>
                </motion.div>
            </div>

            {/* Rotating facts */}
            <div className="w-full max-w-md px-4">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={factIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.4 }}
                        className="text-center"
                    >
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            {LOADING_FACTS[factIndex]}
                        </p>
                    </motion.div>
                </AnimatePresence>

                {/* Fact progress dots */}
                <div className="flex items-center justify-center gap-1.5 mt-4">
                    {LOADING_FACTS.map((_, idx) => (
                        <motion.div
                            key={idx}
                            className={`h-1 rounded-full transition-all duration-300 ${idx === factIndex
                                ? "w-4 bg-foreground"
                                : "w-1 bg-muted-foreground/30"
                                }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );

    if (fullScreen) {
        return (
            <div className="flex items-center justify-center h-screen bg-background">
                {content}
            </div>
        );
    }

    return content;
}

// Simpler inline loading indicator for smaller contexts
export function LoadingSpinner({
    size = "md",
    className = "",
}: {
    size?: "sm" | "md" | "lg";
    className?: string;
}) {
    const sizeClasses = {
        sm: "w-4 h-4",
        md: "w-6 h-6",
        lg: "w-8 h-8",
    };

    return (
        <motion.div
            className={`${sizeClasses[size]} ${className}`}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
            <svg viewBox="0 0 24 24" fill="none" className="w-full h-full text-primary">
                <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="opacity-20"
                />
                <path
                    d="M12 2 A10 10 0 0 1 22 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                />
            </svg>
        </motion.div>
    );
}
